import React from 'react';

import debounce from 'lodash/debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidV4 } from 'uuid';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import { type WrappedComponentProps, injectIntl } from 'react-intl';
import type { CustomData } from '@atlaskit/ufo/types';
import type { UFOExperience } from '@atlaskit/ufo/experience';
import { UFOExperienceState } from '@atlaskit/ufo/experience-state';
import { UserPicker } from '@atlaskit/user-picker/components/user-picker';
import type { OptionData } from '@atlaskit/user-picker/types';
import { isExternalUser } from '@atlaskit/user-picker/is-external-user';
import { isTeam } from '@atlaskit/user-picker/is-team';
import { isGroup } from '@atlaskit/user-picker/is-group';
import { isUser } from '@atlaskit/user-picker/is-user';
import { isValidEmail } from '@atlaskit/user-picker/components/email-validation';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { createAndFireEventInElementsChannel, type SmartEventCreator } from '../analytics';
import { failedRequestUsersEvent } from '../failedRequestUsersEvent';
import { failedUserResolversEvent } from '../failedUserResolversEvent';
import { filterUsersEvent } from '../filterUsersEvent';
import { mountedWithPrefetchEvent } from '../mountedWithPrefetchEvent';
import { preparedUsersLoadedEvent } from '../preparedUsersLoadedEvent';
import { requestUsersEvent } from '../requestUsersEvent';
import { successfulRequestUsersEvent } from '../successfulRequestUsersEvent';
import MessagesIntlProvider from './MessagesIntlProvider';
import {
	type SmartProps,
	type Props,
	type State,
	type FilterOptions,
	UserEntityType,
} from '../types';
import getUserRecommendations from '../service/recommendation-client';
import hydrateDefaultValues from '../service/default-value-hydration-client';
import { type SUPError } from '../service/recommendation-client';
import { smartUserPickerOptionsShownUfoExperience } from '../ufoExperiences';
import type { DebouncedFunc } from 'lodash';

const DEFAULT_DEBOUNCE_TIME_MS = 150;

type EndStateConfig = {
	force?: boolean;
	metadata?: CustomData;
};

const ufoEndStateConfig = (fieldId: string): EndStateConfig => {
	return {
		metadata: {
			contextType: fieldId,
		},
	};
};

const hasContextChanged = (oldContext: SmartProps, newContext: SmartProps): boolean =>
	oldContext.siteId !== newContext.siteId ||
	oldContext.orgId !== newContext.orgId ||
	oldContext.userbaseId !== newContext.userbaseId ||
	oldContext.productKey !== newContext.productKey ||
	oldContext.principalId !== newContext.principalId ||
	oldContext.containerId !== newContext.containerId ||
	oldContext.objectId !== newContext.objectId ||
	oldContext.childObjectId !== newContext.childObjectId;

const stringContains = (str: string | null | void, substr?: string | null) => {
	if (str === null || str === undefined) {
		return false;
	}

	if (substr === null || substr === '' || substr === undefined) {
		return true;
	}

	return str.toLowerCase().includes(substr.toLowerCase());
};

const getUsersForAnalytics = (users: OptionData[]) =>
	(users || []).map(({ id, type }) => ({
		id,
		type,
	}));

const checkIf500Event = (statusCode: number) => 500 <= statusCode && statusCode < 600;

const isEmailQuery = (query: string): boolean => {
	return isValidEmail(query.trim()) === 'VALID';
};

export class SmartUserPickerWithoutAnalytics extends React.Component<
	Props & WrappedComponentProps,
	State
> {
	state: State = {
		users: [],
		loading: false,
		closed: true,
		query: '',
		defaultValue: [],
		bootstrapOptions: [],
	};

	// Track if the last search was an email search that found matches
	private lastEmailSearchFoundMatches = false;

	// Track if the domain-suggested email (`<query>@<suggestEmailsForDomain>`) for a partial
	// query already belongs to a returned user, in which case the suggestion is redundant
	private lastDomainSuggestionMatchedUser = false;

	optionsShownUfoExperienceInstance: UFOExperience;

	static defaultProps: {
		baseUrl: string;
		includeUsers: boolean;
		includeGroups: boolean;
		includeTeams: boolean;
		includeTeamsUpdates: boolean;
		includeNonLicensedUsers: boolean;
		displayEmailInByline: boolean;
		prefetch: boolean;
		principalId: string;
		debounceTime: number;
		userResolvers: never[];
		enableEmailSearch: boolean;
		allowEmailSelectionWhenEmailMatched: boolean;
		verifiedTeams: boolean;
	} = {
		baseUrl: '',
		includeUsers: true,
		includeGroups: false,
		includeTeams: false,
		includeTeamsUpdates: false,
		includeNonLicensedUsers: false,
		displayEmailInByline: false,
		prefetch: false,
		principalId: 'Context',
		debounceTime: DEFAULT_DEBOUNCE_TIME_MS,
		userResolvers: [],
		enableEmailSearch: false,
		allowEmailSelectionWhenEmailMatched: true,
		verifiedTeams: false,
	};

	constructor(props: Props & WrappedComponentProps) {
		super(props);
		this.optionsShownUfoExperienceInstance = smartUserPickerOptionsShownUfoExperience.getInstance(
			props.inputId || props.fieldId,
		);
	}

	async componentDidMount(): Promise<void> {
		try {
			const value = await hydrateDefaultValues(
				this.props.baseUrl,
				this.props.defaultValue,
				this.props.productKey,
				this.props.siteId,
				{ tenantId: this.props.siteId, activationId: this.props.activationId },
			);

			this.setState({
				defaultValue: value,
			});
		} catch (e) {
			const defaultValue: OptionData[] = await (this.props.onValueError
				? this.props.onValueError(e, this.props.defaultValue) || Promise.resolve([])
				: Promise.resolve([]));
			this.setState({ defaultValue: defaultValue });
		}

		const { prefetch } = this.props;
		if (prefetch) {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			const sessionId = uuidV4();
			this.fireEvent(mountedWithPrefetchEvent, { sessionId });
			this.setState({
				sessionId,
			});
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State): void {
		if (hasContextChanged(prevProps, this.props) || this.props.fieldId !== prevProps.fieldId) {
			this.setState({
				users: [],
			});
		}
		if (
			(this.state.sessionId !== prevState.sessionId || this.state.query !== prevState.query) &&
			(this.state.query !== '' || !this.props.bootstrapOptions)
		) {
			this.getUsers();
		} else if (!this.state.closed && !this.state.loading) {
			// If the component has rendered (including its dropdown list) and it
			// is not loading anything further, send the success UFO event
			if (
				![
					UFOExperienceState.FAILED.id,
					UFOExperienceState.SUCCEEDED.id,
					UFOExperienceState.ABORTED.id,
				].includes(this.optionsShownUfoExperienceInstance.state.id)
			) {
				this.optionsShownUfoExperienceInstance.success(ufoEndStateConfig(this.props.fieldId));
			}
		}
	}

	abortOptionsShownUfoExperience = (): void => {
		if (this.optionsShownUfoExperienceInstance.state.id === UFOExperienceState.STARTED.id) {
			// There may be an existing UFO timing running from previous key entry or focus,
			// so abort it and restart it just in case.
			this.optionsShownUfoExperienceInstance.abort();
		}
	};

	startOptionsShownUfoExperience = (): void => {
		this.abortOptionsShownUfoExperience();
		this.optionsShownUfoExperienceInstance.start();
	};

	private fireEvent = (eventCreator: SmartEventCreator, ...args: any[]) => {
		const { createAnalyticsEvent } = this.props;
		if (createAnalyticsEvent) {
			createAndFireEventInElementsChannel(eventCreator(this.props, this.state, ...args))(
				createAnalyticsEvent,
			);
		}
	};

	filterOptions = (
		users: OptionData[],
		query: string,
		propFilterOptions?: FilterOptions,
	): OptionData[] => (propFilterOptions ? propFilterOptions(users, query) : users);

	memoizedFilterOptions: MemoizedFn<
		(users: OptionData[], query: string, propFilterOptions?: FilterOptions) => OptionData[]
	> = memoizeOne(this.filterOptions);

	getUsers: DebouncedFunc<() => Promise<void>> = debounce(async (): Promise<void> => {
		const { query, sessionId, closed } = this.state;

		const {
			baseUrl,
			childObjectId,
			containerId,
			fieldId,
			includeGroups,
			includeTeams,
			includeTeamsUpdates,
			includeUsers,
			includeNonLicensedUsers,
			intl,
			fetchOptions,
			maxOptions,
			objectId,
			onEmpty,
			onError,
			overrideByline,
			displayEmailInByline,
			verifiedTeams,
			orgId,
			userbaseId,
			principalId,
			productAttributes,
			productKey,
			restrictTo,
			searchQueryFilter,
			siteId,
			isTeamSyncedToGroupDirectoryFilter,
			transformOptions,
			userResolvers,
			enableEmailSearch,
		} = this.props;

		const maxNumberOfResults = maxOptions || 100;
		const startTime = window.performance.now();

		// Check if this is an email search
		const isEmail = enableEmailSearch && isEmailQuery(query);

		const recommendationsRequest = {
			baseUrl,
			context: {
				containerId,
				contextType: fieldId,
				objectId,
				principalId,
				productKey,
				siteId,
				organizationId: orgId,
				userbaseId,
				childObjectId,
				sessionId,
				productAttributes,
			},
			includeUsers,
			// For email searches, disable groups and teams
			includeGroups,
			includeTeams,
			includeNonLicensedUsers,
			maxNumberOfResults,
			query,
			searchEmail: isEmail,
			...(verifiedTeams === true && { verifiedTeams: true }),
			/*
				For email-based searches, we have decided to filter out apps.
				Also - because the other 2 filters ((NOT not_mentionable:true) AND (account_status:active)) are included
				when filter is empty, they have been added here to maintain consistency.

				Further ref: https://developer.atlassian.com/platform/user-recommendations/guides/frequently-asked-questions/#filter-behavior
			 */
			searchQueryFilter:
				isEmail && !searchQueryFilter
					? '(NOT not_mentionable:true) AND (account_status:active) AND (NOT account_type:app)'
					: searchQueryFilter,
			...(restrictTo && { restrictTo }),
			...(isTeamSyncedToGroupDirectoryFilter === true && {
				isTeamSyncedToGroupDirectoryFilter: true,
			}),
		};
		try {
			const { query } = this.state;
			this.fireEvent(requestUsersEvent);

			let recommendedUsers;
			if (fetchOptions && fg('smart-user-picker-load-options-gate')) {
				recommendedUsers = await fetchOptions(query);
			} else {
				const userRecommendationsPromise = getUserRecommendations(recommendationsRequest, intl);

				const userResolversPromises = (userResolvers ?? []).map((resolver) =>
					resolver(query).catch((error) => {
						this.fireEvent(failedUserResolversEvent, {
							resolverName: resolver.name,
							error,
						});

						return [] as OptionData[];
					}),
				);

				const [mainRecommendations, ...userResolverResults] = await Promise.all([
					userRecommendationsPromise,
					...userResolversPromises,
				]);

				recommendedUsers = [mainRecommendations, ...userResolverResults].flat();
			}

			if (overrideByline) {
				for (let option of recommendedUsers) {
					if (isUser(option) || isExternalUser(option) || isTeam(option)) {
						option.byline = overrideByline(option);
					}
				}
			}

			if (displayEmailInByline) {
				for (let option of recommendedUsers) {
					if (isUser(option) || isExternalUser(option)) {
						if (
							option.userType === UserEntityType.DEFAULT ||
							option.userType === UserEntityType.CUSTOMER
						) {
							// Respect existing byline if present
							if (option.email && !option.byline) {
								option.byline = option.email;
							}
						}
					}
				}
			}

			if (includeTeamsUpdates) {
				for (let option of recommendedUsers) {
					if (isGroup(option) || isTeam(option)) {
						option.includeTeamsUpdates = includeTeamsUpdates;
					}
				}
			}

			// Track if email search found matches for conditional allowEmail logic
			if (isEmail) {
				// Only count user/external user matches, not teams or groups
				const userMatches = recommendedUsers.filter((user) => isUser(user) || isExternalUser(user));
				this.lastEmailSearchFoundMatches = userMatches.length > 0;
			} else {
				this.lastEmailSearchFoundMatches = false;
			}

			// For a partial query with a suggested domain, check whether the email we would
			// synthesize (`<query>@<domain>`) already belongs to one of the returned users. If so,
			// the real user option is shown instead and the email suggestion is suppressed.
			const { suggestEmailsForDomain } = this.props;
			if (suggestEmailsForDomain && !isEmail && query) {
				const suggestedEmail = `${query}@${suggestEmailsForDomain}`.toLowerCase();
				this.lastDomainSuggestionMatchedUser = recommendedUsers.some(
					(option) =>
						(isUser(option) || isExternalUser(option)) &&
						option.email?.toLowerCase() === suggestedEmail,
				);
			} else {
				this.lastDomainSuggestionMatchedUser = false;
			}

			const elapsedTimeMilli = window.performance.now() - startTime;

			const transformedOptions = transformOptions
				? await transformOptions(recommendedUsers, query)
				: recommendedUsers;

			const displayedUsers =
				transformedOptions.length === 0 && onEmpty
					? ((await onEmpty(query)) ?? [])
					: transformedOptions;

			this.setState((state) => {
				const applicable = state.query === query;
				const users = applicable ? displayedUsers : state.users;
				const loading = !applicable;

				this.fireEvent(successfulRequestUsersEvent, {
					users: getUsersForAnalytics(recommendedUsers),
					elapsedTimeMilli,
					displayedUsers: getUsersForAnalytics(displayedUsers),
					productAttributes,
					applicable,
					...{
						userResolvers: Array.isArray(userResolvers)
							? userResolvers.map((resolver) => resolver.name)
							: [],
					},
				});

				return { users, loading };
			});
		} catch (e) {
			if (this.state.query !== query && fg('smart-user-picker-fetch-error-fix')) {
				// The query has moved on since this request was sent, so its results are not the ones
				// on screen. Applying the failure here would clear the options and fail the UFO
				// experience that belong to the newer query.
				this.fireEvent(failedRequestUsersEvent, {
					elapsedTimeMilli: window.performance.now() - startTime,
					productAttributes,
				});
				return;
			}

			const is5xxEvent = checkIf500Event((e as SUPError).statusCode);
			if (!closed && !onError && is5xxEvent) {
				// If the user lookup fails while the menu is open, and the consumer is not providing a
				// fallback data source via the onError prop, then send UFO failure
				this.optionsShownUfoExperienceInstance.failure(ufoEndStateConfig(this.props.fieldId));
			}
			this.setState({ users: [] });

			let onErrorProducedError = false;
			let defaultUsers: OptionData[] = [];
			try {
				defaultUsers = onError ? (await onError(e, recommendationsRequest)) || [] : [];
			} catch {
				onErrorProducedError = true;
			}
			if (onErrorProducedError && is5xxEvent) {
				// Log error from fallback data source `onError` to UFO
				this.optionsShownUfoExperienceInstance.failure(ufoEndStateConfig(this.props.fieldId));
			}
			if (!is5xxEvent && ((!onError && !closed) || onErrorProducedError)) {
				this.abortOptionsShownUfoExperience();
			}

			this.setState({ users: defaultUsers, loading: false });

			const elapsedTimeMilli = window.performance.now() - startTime;
			this.fireEvent(failedRequestUsersEvent, {
				elapsedTimeMilli,
				productAttributes,
			});
		}
	}, this.props.debounceTime ?? 0);

	onInputChange = (newQuery?: string, sessionId?: string): void => {
		const query = newQuery || '';
		const { closed } = this.state;
		if (query === this.state.query) {
			return;
		}
		if (!closed) {
			// If the input has been typed into and the dropdown has not been closed
			// (i.e. input blurred) then start the UFO timer.
			// If there's a previous UFO timer running for the same "options shown" experience,
			// it will be aborted first.
			this.startOptionsShownUfoExperience();

			this.setState({ loading: true, query, sessionId });
			if (this.props.onInputChange) {
				this.props.onInputChange(query, sessionId);
			}
		}
	};

	filterUsers = (): OptionData[] => {
		const { loading, users, query } = this.state;
		const filteredUsers = this.memoizedFilterOptions(users, query, this.props.filterOptions);
		//If bootstrapOptions have been passed in and it is bootstrap
		if (this.props.bootstrapOptions && this.props.bootstrapOptions.length !== 0 && query === '') {
			const bootstrapFilteredUsers = this.memoizedFilterOptions(
				this.props.bootstrapOptions,
				query,
				this.props.filterOptions,
			);
			this.fireEvent(filterUsersEvent, {
				filtered: getUsersForAnalytics(bootstrapFilteredUsers),
				all: getUsersForAnalytics(this.props.bootstrapOptions),
			});
			return bootstrapFilteredUsers;
		}
		// while when not loading just return already filtered result from server.
		if (!loading) {
			return filteredUsers;
		}
		const queryFilteredUsers = filteredUsers.filter((user: OptionData) =>
			stringContains(user.name, query),
		);
		this.fireEvent(filterUsersEvent, {
			filtered: getUsersForAnalytics(queryFilteredUsers),
			all: getUsersForAnalytics(users),
		});

		// when loading filter previous result.
		return filteredUsers;
	};

	onFocus = (sessionId?: string): void => {
		const state: Partial<State> = { closed: false };
		this.startOptionsShownUfoExperience();
		if (this.state.users.length === 0) {
			state.sessionId = sessionId;
			state.loading = true;
		} else {
			this.fireEvent(preparedUsersLoadedEvent, {
				users: getUsersForAnalytics(this.state.users),
				preparedSessionId: this.state.sessionId,
				sessionId,
			});
		}
		this.setState((currentState) => ({ ...currentState, ...state }));
		if (this.props.onFocus) {
			this.props.onFocus(sessionId);
		}
	};

	onBlur = (sessionId?: string): void => {
		this.getUsers.cancel();

		this.abortOptionsShownUfoExperience();

		// clear old users if query is populated so that on refocus,
		// the old list is not shown
		const users = this.state.query.length === 0 ? this.state.users : [];
		this.setState({ loading: false, closed: true, users });
		if (this.props.onBlur) {
			this.props.onBlur(sessionId);
		}
	};

	render(): React.JSX.Element {
		const {
			allowEmail,
			enableEmailSearch,
			allowEmailSelectionWhenEmailMatched,
			suggestEmailsForDomain,
			...restProps
		} = this.props;

		// Determine whether to allow email selection based on allowEmailSelectionWhenEmailMatched, if needed
		let shouldAllowEmail = allowEmail;

		// When suggestEmailsForDomain is active, partial (non-email) input must still reach the base
		// picker's domain-suggestion creatable logic, so the isEmailQuery restriction is relaxed for
		// those queries only. Full email queries that matched an existing user remain suppressed.
		const isDomainSuggestionEnabled =
			fg('smart_user_picker_suggest_emails_for_domain') && !!suggestEmailsForDomain;

		if (allowEmail && enableEmailSearch && !allowEmailSelectionWhenEmailMatched) {
			const isCurrentQueryEmail = isEmailQuery(this.state.query);

			if (isDomainSuggestionEnabled) {
				// Allow email for partial queries so the "<query>@<domain>" suggestion can render,
				// but still suppress selection when a full email query matched an existing user,
				// or when the synthesized "<query>@<domain>" is itself an existing user's email.
				shouldAllowEmail = isCurrentQueryEmail
					? !this.lastEmailSearchFoundMatches // full email: suppress when user matched
					: !this.lastDomainSuggestionMatchedUser; // partial: suppress when synth email matched a user
			} else {
				// Only allow email selection when:
				// 1. The query matches email format (validated by regex)
				// 2. No user/external user matches were found (only teams/groups suggested)
				shouldAllowEmail = isCurrentQueryEmail && !this.lastEmailSearchFoundMatches;
			}
		}

		return (
			<MessagesIntlProvider>
				<UserPicker
					{...restProps}
					allowEmail={shouldAllowEmail}
					suggestEmailsForDomain={suggestEmailsForDomain}
					onInputChange={this.onInputChange}
					onBlur={this.onBlur}
					onFocus={this.onFocus}
					defaultValue={this.state.defaultValue}
					isLoading={
						this.props.isLoading ||
						(this.state.loading &&
							!this.state.closed &&
							(!this.props.bootstrapOptions || this.state.query !== ''))
					}
					options={this.filterUsers()}
					menuIsOpen={this.props.menuIsOpen}
				/>
			</MessagesIntlProvider>
		);
	}
}

// TODO: Smart User picker team will have to add a type annotation here
export const SmartUserPicker: any = withAnalyticsEvents()(
	injectIntl(SmartUserPickerWithoutAnalytics),
);
